import { get, post, del } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";

const apiName = "compliance-api";

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      return {
        Authorization: `Bearer ${session.tokens.idToken.toString()}`,
      };
    }
    return {};
  } catch (error) {
    console.log("Error getting auth headers:", error);
    return {};
  }
}

export async function getAttestClients(
  financial_professional = "",
  last_evaluated_key = ""
) {
  const authHeaders = await getAuthHeaders();

  // Base64url encode the FP name for lossless transmission through HTTP headers
  // This preserves the exact group-id string (commas, spaces, etc.) so the
  // Python Lambda can do precise contains() matching instead of fuzzy per-word
  const fp = financial_professional.trim();
  const encodedFP = fp
    ? btoa(fp).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
    : "";

  const Init = {
    headers: {
      ...authHeaders,
      financial_professional: encodedFP,
      last_evaluated_key: last_evaluated_key
        .replaceAll("®", "\\u00ae")
        .replaceAll("'", "U0031")
        .replaceAll("-", "U0032"),
    },
  };
  let response = await get({
    apiName,
    path: "/attest",
    options: Init,
  }).response;
  const result = await response.body.json();
  return result;
}

export async function submitClientAttest(client, subChange = "") {
  const authHeaders = await getAuthHeaders();
  const Init = {
    body: {
      type: "submit",
      subChange: subChange,
      client,
    },
    headers: authHeaders,
  };

  let response = await post({
    apiName,
    path: "/attest",
    options: Init,
  }).response;
  const result = await response.body.json();
  return result;
}

export async function clearClientAttest(client) {
  const authHeaders = await getAuthHeaders();
  const Init = {
    body: {
      type: "clear",
      client,
    },
    headers: authHeaders,
  };

  let response = await post({
    apiName,
    path: "/attest",
    options: Init,
  }).response;
  const result = await response.body.json();
  return result;
}

export async function archiveClientAttest(client) {
  const authHeaders = await getAuthHeaders();
  const Init = {
    body: {
      type: "archive",
      client,
    },
    headers: authHeaders,
  };

  let response = await del({
    apiName,
    path: "/attest",
    options: Init,
  }).response;
  const result = await response.body.json();
  return result;
}

export async function clientModalRequest(client, message) {
  const authHeaders = await getAuthHeaders();
  const Init = {
    body: {
      type: "request",
      message: message,
      client,
    },
    headers: authHeaders,
  };
  let response = await post({
    apiName,
    path: "/attest",
    options: Init,
  }).response;
  const result = await response.body.json();
  return result;
}

export async function feedbackModalMessage(
  message,
  rating = "0 - Not Answered"
) {
  const authHeaders = await getAuthHeaders();
  const Init = {
    body: {
      type: "feedback",
      message: message,
      rating: rating,
    },
    headers: authHeaders,
  };
  let response = await post({
    apiName,
    path: "/attest",
    options: Init,
  }).response;
  const result = await response.body.json();
  return result;
}

/**
 * Export attestation clients to Excel
 * @param {string} exportMode - "filtered" (current view with filters) or "full" (entire database)
 * @param {string} exportType - "summary", "detailed", "account", "tableview", or "both"
 * @param {Object} filterCriteria - Filter state from UI (for "filtered" mode)
 * @param {Array} financialProfessionals - Selected FPs (for admin/compliance filtering)
 * @param {Object} sortColumn - Current table sort { path: "client-name", order: "asc" }
 * @returns {Promise<Object>} - { success, downloadUrl, fileName, recordCount }
 */
export async function exportAttestClients(
  exportMode = "filtered",
  exportType = "both",
  filterCriteria = {},
  financialProfessionals = [],
  sortColumn = { path: "client-name", order: "asc" }
) {
  const authHeaders = await getAuthHeaders();

  const Init = {
    body: {
      type: "export",
      ExportMode: exportMode,
      ExportType: exportType,
      Filters: filterCriteria,
      FinancialProfessionals: financialProfessionals,
      SortColumn: sortColumn?.path || "client-name",
      SortOrder: sortColumn?.order || "asc",
    },
    headers: authHeaders,
  };

  let response = await post({
    apiName,
    path: "/attest/export",
    options: Init,
  }).response;
  const result = await response.body.json();
  return result;
}
